import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { getAccountStructure, TFPageContent } from '@tf/shared';
import { Box, Button, Checkbox, createStyles, Group, Icon, List, TFMarkdownText, TFNotifier } from '@tf/ui';
import { type SubmitHandler, T } from '@tf/utils';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, useApplication, useApplicationForms, useApplicationMainReview } from '@/components/application';
import {
	getApplicantContext,
	hideApplicantKindConnections,
} from '@/components/application/hideApplicantKindConnections';
import { AccountStructure, ErrorCard, FormLoadingCard, useMenu } from '@/components/shared';
import { useSegmentsQuery, useSubmitApplicationMutation } from '@/core/api';
import { useDefinitions } from '@/core/providers';
import { endClientAreaPaths } from '@/routes/endClientArea/paths';

import { usePageTitle } from '../../usePageTitle';

import { useApplicationNodeIdParam } from './params';

const useStyles = createStyles(() => ({
	markdownContainer: {
		marginLeft: '.5rem',
		'& p': {
			margin: '0',
			fontSize: '.825rem',
			paddingRight: '20px',
		},
	},
}));

export const ApplicationSubmitRoute: React.FC = () => {
	const { _ } = useLingui();
	const applicationNodeId = useApplicationNodeIdParam();
	const application = useApplication();
	const mainReview = useApplicationMainReview();

	const { classes } = useStyles();

	const { defs } = useDefinitions();
	usePageTitle(defs.customization.last_step?.label ?? _(msg`Review and submit`));

	const [isAgreeToTerms, setIsAgreeToTerms] = useState(false);

	const { menuItems } = useMenu();
	const hasFilledAllRequiredForms = menuItems
		.filter((item) => item.type === 'form')
		.every((item) => item.status === 'OK');
	const isSubmitEnabled = isAgreeToTerms && hasFilledAllRequiredForms;

	const forms = useApplicationForms();
	const segmentsQuery = useSegmentsQuery(forms.flatMap((f) => f.segmentIdentities));

	const navigate = useNavigate();

	const submitApplicationMutation = useSubmitApplicationMutation();

	const onSubmit: SubmitHandler = async () => {
		try {
			T.assertNotNullish(segmentsQuery.data, 'no segment containers loaded');

			await submitApplicationMutation.mutateAsync({
				applicationNodeId,
				applicantNodeId: mainReview.graphNodeId,
				consent: {
					label: defs.accounts[application.account.entityKind].consent_checkbox_label,
					description: defs.accounts[application.account.entityKind].consent_messages.join('\n'),
				},
			});

			navigate(endClientAreaPaths.applicationSubmitted());
			window.scrollTo(0, 0);
		} catch (_e) {
			TFNotifier.error(
				_(
					msg({
						id: 'applicationSubmitPage.notification.submitFailed',
						message: 'Failed to submit application',
					}),
				),
			);
		}
	};
	const renderTerms = (style?: React.CSSProperties) => {
		return (
			<Group style={style} mb="1rem" pr="1.5rem">
				<Checkbox
					checked={isAgreeToTerms}
					onChange={(e) => setIsAgreeToTerms(e.target.checked)}
					label={defs.accounts[application.account.entityKind].consent_checkbox_label}
				/>
				<List className={classes.markdownContainer} size="sm" spacing="xs">
					{defs.accounts[application.account.entityKind].consent_messages.map((message) => (
						<List.Item key={message}>
							<TFMarkdownText key={message} text={message} />
						</List.Item>
					))}
				</List>
			</Group>
		);
	};

	if (segmentsQuery.isPending) {
		return <FormLoadingCard />;
	}

	if (segmentsQuery.error) {
		return (
			<ErrorCard
				icon={<Icon.IconExclamationCircle />}
				title={_(
					msg({
						id: 'applicationSubmitPage.loadingError.title',
						message: 'Failed to load application',
					}),
				)}
				description={_(
					msg({
						id: 'applicationSubmitPage.loadingError.message',
						message: 'Please try again',
					}),
				)}
				primaryAction={
					<Button mt="1rem" type="button" onClick={() => segmentsQuery.refetch()}>
						{_(
							msg({
								id: 'applicationSubmitPage.loadingError.action',
								message: 'Reload application',
							}),
						)}
					</Button>
				}
			/>
		);
	}

	return (
		<TFPageContent maw="50rem" m="0">
			<Form
				key="submit"
				readOnly
				label={defs.customization.last_step?.label ?? _(msg`Review and submit`)}
				description={
					defs.customization.last_step?.description ??
					_(
						msg({
							id: 'applicationSubmitPage.description',
							message:
								'Nearly done! Please review your answers and click ‘Submit application’ once you are happy to proceed.',
						}),
					)
				}
				segmentContainers={segmentsQuery.data?.map((d) => d.container)}
				renderPreSubmitSection={() => {
					const accountStructure = hideApplicantKindConnections(
						getAccountStructure({
							account: {
								meta: application.account,
								reviews: application.reviews,
							},
							showRootNode: true,
							entitiesDefs: defs.entities,
						}),
						getApplicantContext(
							mainReview,
							defs.accounts[application.account.entityKind].connection_filters,
							application.account.graphNodeId,
						),
					);

					return (
						<Box sx={{ height: '500px' }}>
							<AccountStructure accountStructure={accountStructure} />
						</Box>
					);
				}}
				renderSubmit={(isLoading) => {
					return (
						<Box w="100%">
							<RenderAtTheEndOfPage
								renderBeforeTheEnd={() => (
									<Box w="100%" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
										<Button
											onClick={() => {
												window.scrollTo({
													top: document.body.scrollHeight,
													behavior: 'smooth',
												});
											}}
										>
											<Trans id="applicationSubmitPage.submitButton">Submit application</Trans>
										</Button>
									</Box>
								)}
								renderAtTheEnd={() => (
									<Box p="12px" w="100%">
										{renderTerms()}
										<Box w="100%" sx={{ display: 'flex', justifyContent: 'flex-end' }}>
											<Button disabled={!isSubmitEnabled} type="submit" loading={isLoading}>
												<Trans id="applicationSubmitPage.submitButton">Submit application</Trans>
											</Button>
										</Box>
									</Box>
								)}
							/>
						</Box>
					);
				}}
				onSubmit={onSubmit}
			/>
			{/*we need to render this dummy block to maintain a consistent body.offsetHeight.
			 without this, using scroll to bottom and conditional rendering of terms of agreement may lead to a weird behavior*/}
			<RenderAtTheEndOfPage renderDummyComponent={() => renderTerms({ visibility: 'hidden', padding: '24px' })} />
		</TFPageContent>
	);
};

interface RenderAtTheEndOfPageProps {
	renderBeforeTheEnd?: () => React.ReactNode;
	renderAtTheEnd?: () => React.ReactNode;
	renderDummyComponent?: () => React.ReactNode;
}

export const RenderAtTheEndOfPage: React.FC<RenderAtTheEndOfPageProps> = ({
	renderBeforeTheEnd,
	renderAtTheEnd,
	renderDummyComponent,
}) => {
	const [isEndOfPage, setIsEndOfPage] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 150) {
				setIsEndOfPage(true);
			} else {
				setIsEndOfPage(false);
			}
		};

		window.addEventListener('scroll', handleScroll);
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	if (!isEndOfPage) {
		return (
			<>
				{renderBeforeTheEnd?.()}
				{renderDummyComponent?.()}
			</>
		);
	}

	return <>{renderAtTheEnd?.()}</>;
};
